import { useAtomValue, useSetAtom } from "jotai";
import React, { useCallback, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import * as CONFIG from "../../config";
import { navFor } from "../../dashboard/select";
import useDashboardCtx from "../../dashboard/useDashboardCtx";
import { useStateValue } from "../../globalState";
import { useCartProducts, useShrinkBuffer } from "../../hooks";
import { hasSidebarAtom, sidebarOpenAtom } from "../../navigation/sidebarAtom";
import {
  Badge,
  Button,
  CartIcon,
  DashboardIcon,
  MenuIcon as HamburgerIcon,
  IconButton,
} from "../../themeV2";
import AccountDashboardMenu from "../AccountDashboardMenu";
import Search from "../Browse/SearchV2";
import MobileSidebarModal from "../MobileSidebarModal";
import SiteNotice from "../SiteNotice";
import css from "./Navbar.module.css";

const Navbar = React.memo(function Navbar() {
  const location = useLocation();
  const [{ user }] = useStateValue();
  const { cartProducts } = useCartProducts();

  // mobile sidebar vars
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const { showMobileNav } = useShrinkBuffer();

  const setSidebarOpen = useSetAtom(sidebarOpenAtom);
  const hasSidebar = useAtomValue(hasSidebarAtom);
  const dashboardCtx = useDashboardCtx();
  const hasMobileNav =
    hasSidebar ||
    Boolean(dashboardCtx.activeInfluencer) ||
    navFor("mobile", dashboardCtx).length > 0;

  const handleHamburgerClick = useCallback(() => {
    if (hasSidebar) {
      setSidebarOpen((prev) => !prev);
    } else {
      setShowMobileSidebar((prev) => !prev);
    }
  }, [hasSidebar, setSidebarOpen]);

  const closeMobileSidebar = useCallback(() => setShowMobileSidebar(false), []);

  const loginPath = `/login?${new URLSearchParams({
    r: `${location.pathname}${location.search}${location.hash}`,
    landing: "1",
  })}`;

  return (
    <>
      <a href="#main" className={css.skipLink}>
        Skip to main content
      </a>
      <header className={css.navbar}>
        {showMobileNav &&
          hasMobileNav &&
          !user.is_approval_pending &&
          user.user_id && (
            <IconButton
              label="Open navigation menu"
              className={css.hamburger}
              onClick={handleHamburgerClick}
            >
              <HamburgerIcon />
            </IconButton>
          )}

        <NavLink to="/" className={css.logoLink}>
          <img
            className={css.logo}
            width="108"
            height="40"
            alt="Streamily home"
            src={CONFIG.SITE_LOGO}
          />
        </NavLink>

        <div role="search" className={css.search}>
          <Search />
        </div>

        <div className={css.right}>
          {!showMobileNav &&
            !user.is_approval_pending &&
            user.show_dashboard_link && (
              <Button
                as={NavLink}
                variant="text"
                className={css.dashboardLink}
                to="/dashboard"
                leading={<DashboardIcon />}
              >
                <span className={css.dashboardLinkText}>Dashboard</span>
              </Button>
            )}

          <div className={css.cartWrapper}>
            <IconButton
              label="Open cart"
              onClick={() => {
                document.dispatchEvent(new CustomEvent("cart:open"));
              }}
            >
              <CartIcon />
            </IconButton>
            {cartProducts.length > 0 && (
              <Badge
                className={css.cartBadge}
                variant="count"
                count={cartProducts.length}
              />
            )}
          </div>

          {!user.is_approval_pending && user.user_id ? (
            <AccountDashboardMenu user={user} />
          ) : user.is_approval_pending ? (
            <>
              <Button
                as={NavLink}
                variant="text"
                className={css.textLink}
                to="/talent/pending"
              >
                Dashboard
              </Button>
              <AccountDashboardMenu user={user} />
            </>
          ) : (
            <Button
              as={NavLink}
              variant="text"
              className={css.textLink}
              to={loginPath}
            >
              Log In
            </Button>
          )}
        </div>
      </header>

      {showMobileSidebar && !hasSidebar && (
        <MobileSidebarModal onClose={closeMobileSidebar} />
      )}

      <SiteNotice />
    </>
  );
});

export default Navbar;
